import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { createStory, generateStorySlug } from "@/lib/api/stories.functions";
import { AdminFormFooter } from "@/components/admin/AdminFormFooter";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { SlugField } from "@/components/admin/SlugField";
import { ContentAdminTabs } from "@/components/admin/ContentAdminTabs";
import { BlockDocEditor } from "@/components/admin/doc/BlockDocEditor";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MediaField } from "@/components/admin/MediaPicker";
import type { MediaSelection } from "@/lib/media/types";
import { emptyEditorDoc, type EditorDoc } from "@/lib/doc/doc-types";

export const Route = createFileRoute("/admin/stories/new")({
  component: NewStoryPage,
});

function NewStoryPage() {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [client, setClient] = useState("");
  const [industry, setIndustry] = useState("");
  const [service, setService] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [editorDoc, setEditorDoc] = useState<EditorDoc>(emptyEditorDoc());
  const [outcomes, setOutcomes] = useState([{ label: "", value: "" }]);
  const [cover, setCover] = useState<MediaSelection>({});
  const [ogImage, setOgImage] = useState<MediaSelection>({});
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      const row = await createStory({
        data: {
          title,
          slug,
          client,
          industry,
          service,
          excerpt,
          editorDoc,
          outcomes: outcomes.filter((o) => o.label),
          status: "draft",
          coverMediaId: cover.mediaId,
          coverUrl: cover.url,
          ogImageMediaId: ogImage.mediaId,
        },
      });
      toast.success("Story created");
      navigate({ to: "/admin/stories/$id", params: { id: row.id } });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to create");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminPageShell>
      <AdminPageHeader title="New client story" />
      <ContentAdminTabs
        settingsLabel="Story settings"
        settingsForm={
          <div className="space-y-4">
            <div className="space-y-2"><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} /></div>
            <SlugField title={title} slug={slug} onSlugChange={setSlug} onGenerate={() => generateStorySlug({ data: { title } })} />
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2"><Label>Client</Label><Input value={client} onChange={(e) => setClient(e.target.value)} /></div>
              <div className="space-y-2"><Label>Industry</Label><Input value={industry} onChange={(e) => setIndustry(e.target.value)} /></div>
              <div className="space-y-2"><Label>Service</Label><Input value={service} onChange={(e) => setService(e.target.value)} /></div>
            </div>
            <div className="space-y-2"><Label>Excerpt</Label><Textarea value={excerpt} onChange={(e) => setExcerpt(e.target.value)} /></div>
            <div className="space-y-2">
              <Label>Outcomes (KPI strip)</Label>
              {outcomes.map((o, i) => (
                <div key={i} className="grid gap-2 sm:grid-cols-2">
                  <Input
                    placeholder="Label"
                    value={o.label}
                    onChange={(e) => {
                      const next = [...outcomes];
                      next[i] = { ...next[i], label: e.target.value };
                      setOutcomes(next);
                    }}
                  />
                  <Input
                    placeholder="Value"
                    value={o.value}
                    onChange={(e) => {
                      const next = [...outcomes];
                      next[i] = { ...next[i], value: e.target.value };
                      setOutcomes(next);
                    }}
                  />
                </div>
              ))}
              <button
                type="button"
                className="text-sm font-semibold text-primary hover:underline"
                onClick={() => setOutcomes([...outcomes, { label: "", value: "" }])}
              >
                + Add outcome
              </button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <MediaField label="Cover image" value={cover} onChange={setCover} />
              <MediaField label="OG image" value={ogImage} onChange={setOgImage} />
            </div>
          </div>
        }
        content={
          <BlockDocEditor initialDoc={editorDoc} onChange={setEditorDoc} className="w-full" />
        }
      />
      <AdminFormFooter cancelTo="/admin/stories" onSave={handleSave} saving={saving} saveLabel="Create story" savingLabel="Creating…" />
    </AdminPageShell>
  );
}
